import * as React from 'react';
import { styled } from '@glitz/react';
import { translate } from '@avensia/excite';
import { useCustomerType } from '@avensia/excite-commercetools';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { InfoIcon } from 'webapp/components/icons';
import { useFormatPrice } from '@avensia/excite-ui';

export type PriceConfig = {
  showRedPrice?: boolean;
  showOriginalPrice?: boolean;
  hideOriginalIfSame?: boolean;
  showComparePrice?: boolean;
  showPriceInfoModal?: boolean;
  showComparePriceType?: NordicNest.Api.ComparePriceType;
  showComparePriceStrikethrough?: boolean;
  showComparePriceSuffix?: boolean;
  lowestHistoricalPrice?: Excite.Commerce.AmountWithVatApiModel;
  vatActive?: boolean;
};

type PropType = {
  price: NordicNest.Api.PriceApiModel;
  config?: PriceConfig;
  showPriceMatch: boolean;
  setShowModal: (show: boolean) => void;
};

export const Price = styled(({ price, config, showPriceMatch, setShowModal }: PropType) => {
  const { isCompanyCustomer } = useCustomerType();
  const formatPrice = useFormatPrice();
  const comparePrice =
    config?.showComparePriceType === 'originalPrice' ? price?.regular : config?.lowestHistoricalPrice;
  const originalPrice = price?.regular;
  const sameAsRef = originalPrice?.inclVat === config?.lowestHistoricalPrice?.inclVat;
  const showOriginalPrice = !(sameAsRef && config?.hideOriginalIfSame) && config?.showOriginalPrice;
  const showInfoIcon =
    config?.showPriceInfoModal && ((showOriginalPrice && price?.regular) || (price?.showComparePrice && comparePrice));
  const comparePriceIsOriginalPrice = config?.showComparePriceType === 'originalPrice';

  const renderComparePrice = () => {
    if (config?.showComparePrice && comparePrice) {
      return (
        <OriginalPriceWrapper
          style={!showPriceMatch ? { display: 'flex', alignItems: 'end', lineHeight: '1.2' } : { marginLeft: 'unset' }}
        >
          <OriginalPrice
            css={config?.showComparePriceStrikethrough ? comparePriceStrikeDecorator : comparePriceDecorator}
            suppressHydrationWarning
            {...NnTestIdProps.ComparePriceElement}
          >
            {isCompanyCustomer ? formatPrice(comparePrice.exclVat) : formatPrice(comparePrice.inclVat)}
          </OriginalPrice>
          {comparePriceIsOriginalPrice && (
            <VatText>
              {config?.showComparePriceSuffix &&
                (isCompanyCustomer || !config?.vatActive
                  ? translate(t => t.order.recommendedRetailPriceExclVat)
                  : translate(t => t.order.recommendedRetailPriceInclVat))}
            </VatText>
          )}
          {price.discount?.length > 0 && <Discount>{`(${price.discount})`}</Discount>}
        </OriginalPriceWrapper>
      );
    }
    return null;
  };

  return (
    <Base>
      <div style={{ display: 'flex' }}>
        <CurrentPrice
          css={config?.showRedPrice && redPriceDecorator}
          {...(config?.showRedPrice ? NnTestIdProps.RedPriceElement : NnTestIdProps.CurrentPriceElement)}
          suppressHydrationWarning
        >
          {formatPrice(isCompanyCustomer ? price.current.exclVat : price.current.inclVat)}
        </CurrentPrice>
        {!showPriceMatch && renderComparePrice()}
        {showInfoIcon && (
          <div
            style={{
              alignItems: 'center',
            }}
          >
            <StyledInfoIcon onClick={() => setShowModal(true)} {...NnTestIdProps.PdpPriceInfoIcon} />
          </div>
        )}
      </div>
      <Original>
        {showPriceMatch && renderComparePrice()}
        {showOriginalPrice && originalPrice && (
          <div>
            <OriginalPrice suppressHydrationWarning {...NnTestIdProps.PdpOriginalPriceElement}>
              {isCompanyCustomer ? formatPrice(originalPrice.exclVat) : formatPrice(originalPrice.inclVat)}
            </OriginalPrice>
            {config?.showComparePriceSuffix && (
              <VatText {...NnTestIdProps.OriginalPricePrefixSuffix}>
                {isCompanyCustomer || !config?.vatActive
                  ? translate(t => t.order.recommendedRetailPriceExclVat)
                  : translate(t => t.order.recommendedRetailPriceInclVat)}
              </VatText>
            )}
          </div>
        )}
      </Original>
    </Base>
  );
});

const Base = styled.div({
  columnGap: t => t.sharedSpacing.m,
  display: 'flex',
  flexDirection: 'column',
});

const StyledInfoIcon = styled(InfoIcon, {
  marginLeft: t => t.sharedSpacing.m,
  width: 18,
  height: 18,
  flexShrink: 0,
  cursor: 'pointer',
  color: '#9F9F9F',
});

export const PriceInfoWrapper = styled.div({
  rowGap: t => t.sharedSpacing.s,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

export const PriceInfoHeaderWrapper = styled.div({
  display: 'flex',
  columnGap: t => t.sharedSpacing.m,
  alignItems: 'center',
});

export const OriginalPriceWrapper = styled.div({
  marginLeft: t => t.sharedSpacing.m,
});

export const PriceInfoHeader = styled.h2({
  fontSize: t => t.sharedFontSize.s,
  fontWeight: t => t.fontWeight?.bold,
  wordWrap: 'break-word',
  marginBottom: 'unset',
  height: 'fit-content',
});

export const PriceInfoText = styled.span({
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => t.fontWeight?.normal,
});

const CurrentPrice = styled.span({
  fontWeight: 500,
  fontSize: t => t.sharedFontSize.l,
  lineHeight: 1.2,
});

const Original = styled.div({
  fontWeight: 300,
  opacity: 0.75,
  display: 'flex',
  flexDirection: 'column',
  fontSize: t => t.sharedFontSize.xs,
});

export const OriginalPrice = styled.span({
  fontSize: t => t.fontSize.xs,
});

const VatText = styled.span({
  marginLeft: t => t.sharedSpacing.xs,
  fontSize: t => t.fontSize.xs,
});

const Discount = styled.span({
  marginLeft: t => t.sharedSpacing.xs,
  fontSize: t => t.fontSize.xs,
  fontWeight: '450 !important',
});

const redPriceDecorator = styled({
  color: t => t.price?.discount?.color,
});

const comparePriceDecorator = styled({
  fontSize: t => t.fontSize.xs,
  fontWeight: '450 !important',
});

const comparePriceStrikeDecorator = styled({
  textDecoration: 'line-through',
  fontSize: t => t.fontSize.xs,
  fontWeight: '450 !important',
});
